// Sprint 35 item 1: the mini-game window. After the reveal of a special knock and its chalk sum, a plate (1.0 s), the game on fixed 15 fps
// steps (no tap to start), the result (1.2 s), then the page chalks one more step. The same window plays the practice room's picker,
// where nothing is at stake. Skip is a 44 px button, Esc and leaving the page; the arrow keys play both games without a pointer.
// All words are real text (the sentence font, never below 14 px), never drawn into the frame. The frame itself is client/minigame-view.js.
import { pickerCards, gameOf, GAME_IDS } from '../game/minigames/registry.js';
import { plateOf, practicePlateOf, resultLine, practiceLine, actionOf } from '../game/minigames/copy.js';
import { seedBytes } from '../game/minigames/seed.js';
import { createRunner, STEP_MS, bullInput, bullKeys, bearTarget, boxTarget, bearKeys, seedOfMessage, FRAME } from './minigame-core.js';
import { drawFrame } from './minigame-view.js';
import { whenArt } from './minigame-art.js';

export const PLATE_MS = 1000, RESULT_MS = 1200, RESULT_WAIT_MS = 3000, ACTION_MS = 900;

// The studio's held bear frames: a doorkeeper who steps out from under every shadow once it has shown for 3 steps (a demo
// only, for screenshots; never a player model and never sent). Returns the touch target [x + 1, y + 1], or [0, 0] to stand.
export function demoDodge(o) {
  const live = o.threats.filter(th => o.t >= th.warnStep + 3 && o.t < th.slamStep + 3).flatMap(th => th.rects);
  const m = 2, hw = o.half.w + m, hh = o.half.h + m;
  const hits = (x, y) => live.some(r => x + hw > r.x0 && x - hw < r.x1 && y + hh > r.y0 && y - hh < r.y1);
  if (!hits(o.me.x, o.me.y)) return [0, 0];
  const xs = [o.me.x, ...live.flatMap(r => [r.x0 - hw, r.x1 + hw])], ys = [o.me.y, ...live.flatMap(r => [r.y1 + hh]), o.box.h - hh];
  let best = null;
  for (const x of xs) for (const y of ys) {
    if (x < o.half.w || x > o.box.w - o.half.w || y < o.half.h || y > o.box.h - o.half.h || hits(x, y)) continue;
    const d = Math.abs(x - o.me.x) + Math.abs(y - o.me.y);
    if (!best || d < best.d) best = { x, y, d };
  }
  return best ? [best.x + 1, best.y + 1] : [0, 0];
}
// The studio's held Hold the bags frames: taps each bag at the step it hangs nearest the pile's middle (a demo only, for screenshots; never a
// player model and never sent). Returns 1 to tap this step.
export function demoTap(o) {
  const h = o.hang;
  if (!h || !h.tappable) return 0;
  const mid = (o.top.l + o.top.r) / 2;
  let best = 0;
  h.path.forEach((x, u) => { if (Math.abs(x - mid) < Math.abs(h.path[best] - mid)) best = u; });
  return h.u === best ? 1 : 0;
}
// STILL HOLDING! is a board 148 x 86 up by the lamp (rows 4 to 90), so once Hold the bags ends on a standing pile the plate's words, read and done with, make room for it
const platePast = (game, res, text) => (game === 'holden' && res.over && !res.tippedBag ? '' : text);
const MODE_WORDS = { bonus: 'Bonus', rescue: 'Rescue' };
const ARROWS = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down', a: 'left', d: 'right', w: 'up', s: 'down', A: 'left', D: 'right', W: 'up', S: 'down' };

export function createMini({ doc, root, send, reduced = () => false, now = () => performance.now(), raf = f => requestAnimationFrame(f), caf = id => cancelAnimationFrame(id),
  set = (f, ms) => setTimeout(f, ms), clear = id => clearTimeout(id), random = () => globalThis.crypto.getRandomValues(new Uint8Array(32)), onDone = () => {} }) {
  // every word sits in a span.t: the page cuts it to one bit (the #bitb filter), so no word has grey or coloured edges
  const word = text => { const t = doc.createElement('span'); t.className = 't'; t.textContent = text; return t; };
  const say = (e, text) => { if (e.children[0]?.className === 't') e.children[0].textContent = text; else e.replaceChildren(word(text)); };
  const el = (tag, cls, text) => { const e = doc.createElement(tag); if (cls) e.className = cls; if (text !== undefined) e.append(word(text)); return e; };
  const btn = (cls, text, onclick) => { const b = el('button', cls, text); b.type = 'button'; b.addEventListener('click', onclick); return b; };

  // ---- the window ----
  const titleWord = word(''), title = el('h2', 'mg-title'), plate = el('p', 'mg-plate', ''), result = el('p', 'mg-result', '');
  plate.setAttribute('aria-live', 'polite'); result.setAttribute('aria-live', 'polite');
  const canvas = el('canvas', 'mg-canvas'); canvas.width = FRAME.w; canvas.height = FRAME.h;
  canvas.setAttribute('role', 'img'); canvas.setAttribute('aria-label', 'The mini-game');
  const titleBg = el('span', 'mg-titlebg'); titleBg.append(titleWord); title.append(titleBg); // the striped bar's paper label, its word cut to one bit
  // the hold-and-slide hint (Aseprite art, client/art/aseprite/mini-ui/mini-hand): shown during the plate, stepped by css
  const hand = el('span', 'mg-hand'); hand.setAttribute('aria-hidden', 'true');
  // the action word (copy.js ACTION), big over the frame as play starts
  const action = el('p', 'mg-action', ''); action.setAttribute('aria-hidden', 'true'); action.hidden = true;
  const frame = el('div', 'mg-frame'); frame.append(canvas, hand, action);
  const skipBtn = btn('mg-skip', 'Skip', () => skip('button'));
  const again = btn('mg-again', 'Again', () => againPractice()), back = btn('mg-back', 'Back', () => (cur?.kind === 'picker' ? close() : backToPicker()));
  const actions = el('div', 'mg-actions'); actions.append(again, back);
  const cards = el('ul', 'mg-cards');
  const win = el('div', 'mg-win'); win.append(title, plate, frame, skipBtn, result, actions, cards);
  root.append(win);
  root.hidden = true;

  let cur = null; // { kind: 'real' | 'practice', game, mode, offer?, runner, phase, ... }
  let offer = null, rafId = null, timers = [], picked = Object.fromEntries(GAME_IDS.map(id => [id, 'bonus']));
  const keys = {}, pointer = { on: false, value: null }, tap = { pending: false }; // tap: Hold the bags, a press waiting for the next step
  const later = (f, ms) => { const id = set(f, ms); timers.push(id); return id; };
  const stopAll = () => { timers.forEach(clear); timers = []; if (rafId !== null) caf(rafId); rafId = null; };
  const show = (e, on) => { e.hidden = !on; };
  const g2d = canvas.getContext?.('2d');
  // the frame is painted from the last observation, so it can be painted again once the Aseprite sheets have loaded
  const paint = () => { if (g2d && cur?.lastObs) cur.frameOps = drawFrame(g2d, cur.game, cur.lastObs, { reduced: reduced(), step: cur.lastObs.t, after: cur.after ?? 0 }); };
  const draw = (after = 0) => { if (g2d && cur?.runner) { cur.lastObs = cur.runner.observe(); cur.after = after; paint(); } };
  whenArt(paint);
  // after the end: the throw's arc, the bell and "UP ONLY!" step on at 15 fps over the result (reduced motion: their last frame)
  const OUTRO = 18;
  function outro() {
    const at = now(), phase = cur?.phase, run = cur?.runner;
    if (reduced()) return draw(OUTRO);
    const tick = () => {
      rafId = null;
      if (!cur || cur.phase !== phase || cur.runner !== run) return;
      const after = Math.min(OUTRO, Math.floor((now() - at) / STEP_MS));
      draw(after);
      if (after < OUTRO) rafId = raf(tick);
    };
    rafId = raf(tick);
  }
  const hint = on => { root.dataset.hint = on ? '1' : '0'; };

  function layout({ plateText = '', frameOn = true, skipOn = false, resultText = '', actionsOn = false, cardsOn = false, win = false }) {
    root.dataset.phase = cur?.phase ?? ''; // for the page's own checks and the studio's shots
    root.dataset.game = cur?.game ?? '';
    say(plate, plateText); say(result, resultText);
    result.dataset.win = win ? '1' : '0'; // the ringing result bell (art) before the line
    show(plate, !!plateText); show(frame, frameOn); show(skipBtn, skipOn); show(result, !!resultText); show(actions, actionsOn); show(cards, cardsOn);
    if (cardsOn && cards.style) cards.style.top = `calc(var(--head) + ${Math.max(72, (plate.offsetHeight || 0) + 12)}px)`; // the cards start under the plate, however many lines it takes
  }
  function open(name) { titleWord.textContent = name; root.hidden = false; root.dataset.reduced = reduced() ? '1' : '0'; }
  function close() { stopAll(); hint(false); action.hidden = true; cur = null; root.hidden = true; for (const k of Object.keys(keys)) delete keys[k]; pointer.on = false; tap.pending = false; }

  // ---- the input: a pointer (hold and slide) or the arrow keys, same trace ----
  const keyValue = obs => (cur.game === 'bull' ? bullKeys(keys) : bearKeys(keys, obs.me));
  const inputNow = obs => (cur.game === 'holden' ? (tap.pending ? ((tap.pending = false), 1) : 0) : pointer.on && pointer.value !== null ? pointer.value : keyValue(obs));
  const point = e => { const r = canvas.getBoundingClientRect?.() ?? { left: 0, top: 0, width: FRAME.w, height: FRAME.h }; return [((e.clientX - r.left) * FRAME.w) / (r.width || FRAME.w), ((e.clientY - r.top) * FRAME.h) / (r.height || FRAME.h)]; };
  function pointerAt(e) {
    const [x, y] = point(e);
    pointer.value = cur?.game === 'bear' ? (y >= FRAME.h - FRAME.pad ? bearTarget(x, y) : boxTarget(x, y) ?? [0, 0]) : bullInput(x - FRAME.w / 2);
  }
  canvas.addEventListener('pointerdown', e => { if (cur?.phase !== 'play') return; hint(false); if (cur.game === 'holden') { tap.pending = true; e.preventDefault?.(); return; } pointer.on = true; pointerAt(e); canvas.setPointerCapture?.(e.pointerId); e.preventDefault?.(); });
  canvas.addEventListener('pointermove', e => { if (pointer.on) pointerAt(e); });
  for (const t of ['pointerup', 'pointercancel']) canvas.addEventListener(t, () => { pointer.on = false; pointer.value = null; });
  doc.addEventListener('keydown', e => {
    if (root.hidden) return;
    if (e.key === 'Escape') { e.preventDefault?.(); return escape(); }
    if (cur?.phase === 'practice-end' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault?.(); return againPractice(); }
    if (cur?.phase === 'play' && cur.game === 'holden' && (e.key === ' ' || e.key === 'Spacebar') && !e.repeat) { tap.pending = true; e.preventDefault?.(); return; } // space drops a bag
    const dir = ARROWS[e.key];
    if (dir && cur?.phase === 'play') { keys[dir] = true; e.preventDefault?.(); }
  });
  doc.addEventListener('keyup', e => { const dir = ARROWS[e.key]; if (dir) keys[dir] = false; });
  doc.addEventListener('pagehide', () => { if (cur?.kind === 'real' && cur.phase !== 'result') send({ t: 'mini-skip', id: cur.offer.id, why: 'left' }); });

  // ---- the loop: steps by the clock, frames by the browser ----
  function loop() {
    rafId = null;
    if (!cur?.runner || cur.phase !== 'play') return;
    cur.runner.tick(now());
    draw();
    if (cur.runner.over) return ended();
    rafId = raf(loop);
  }
  function playStart(seed) {
    cur.runner = createRunner({ gameId: cur.game, seed, mode: cur.mode, start: now() });
    cur.runner.setInput(inputNow);
    tap.pending = false;
    cur.phase = 'play';
    layout({ plateText: cur.plateText, skipOn: true });
    shout(cur.game);
    rafId = raf(loop);
  }
  function shout(game) {
    action.replaceChildren(word(actionOf(game)));
    action.hidden = !actionOf(game);
    root.dataset.action = reduced() ? 'still' : 'pop';
    later(() => { action.hidden = true; }, ACTION_MS);
  }

  // ---- a real game: after the reveal and its sum ----
  function takeOffer(m) { offer = m; }
  function begin() {
    const m = offer;
    if (!m || cur) return false;
    offer = null;
    const text = plateOf(m.game, m.mode);
    cur = { kind: 'real', offer: m, game: m.game, mode: m.mode, phase: 'plate', plateText: text };
    open(gameOf(m.game).NAME);
    layout({ plateText: text, skipOn: true });
    hint(m.game !== 'holden'); // the hand shows hold-and-slide; a tap game has nothing to show
    cur.runner = createRunner({ gameId: m.game, seed: new Uint8Array(gameOf(m.game).STEPS), mode: m.mode, start: 0 }); // the first frame under the plate: the seed is not asked for until the plate has been read
    draw();
    cur.runner = null;
    later(() => { if (cur?.offer === m && cur.phase === 'plate') { hint(false); cur.phase = 'wait'; root.dataset.phase = 'wait'; send({ t: 'mini-start', id: m.id }); } }, PLATE_MS);
    return true;
  }
  function onSeed(m) {
    if (!cur || cur.kind !== 'real' || cur.offer.id !== m.id || cur.phase !== 'wait') return;
    cur.sizes = m.sizes ?? null; // the server's size row: the page claims with the numbers the server pays
    playStart(seedOfMessage(m));
  }
  function ended() {
    if (cur.kind === 'practice') return practiceEnded();
    cur.phase = 'result';
    const trace = cur.runner.fullTrace(), local = cur.runner.score(cur.offer.right, false, cur.sizes), res = cur.runner.result();
    // the run's facts ride with the points; the server replays the trace and only believes what it reproduces
    send({ t: 'mini-trace', id: cur.offer.id, trace, claim: { points: local.points, steps: res.steps, reachedEnd: res.reachedEnd, clean: res.clean, ...(cur.game === 'holden' ? { bags: res.bags } : {}) } });
    cur.local = local;
    cur.plateText = platePast(cur.game, res, cur.plateText);
    layout({ plateText: cur.plateText, resultText: resultLine({ game: cur.game, mode: cur.mode, steps: res.steps, reachedEnd: res.reachedEnd, played: local.played, reason: local.reason, bags: res.bags, tippedBag: res.tippedBag, points: local.points }), skipOn: false, win: res.reachedEnd && local.played });
    cur.shownAt = now();
    outro();
    later(() => finishReal(), RESULT_WAIT_MS);
    later(() => { if (cur?.serverResult) finishReal(); }, RESULT_MS);
  }
  function onResult(m) {
    if (!cur || cur.kind !== 'real' || cur.offer.id !== m.id) return;
    cur.serverResult = m;
    layout({ plateText: cur.plateText, resultText: m.line || result.children[0]?.textContent, win: result.dataset.win === '1' });
    if (cur.phase === 'result' && now() - cur.shownAt >= RESULT_MS) finishReal();
    else if (cur.phase !== 'result') finishReal(); // a late start or a server-side close: no game to show
  }
  function finishReal() {
    if (!cur || cur.kind !== 'real') return;
    const m = cur.serverResult ?? { id: cur.offer.id, game: cur.game, mode: cur.mode, points: 0, chalk: { kind: 'mini', text: '- - -', smear: true } };
    close();
    onDone(m);
  }
  function skip(why = 'button') {
    if (!cur || cur.kind === 'picker') return;
    if (cur.kind === 'practice') return backToPicker();
    if (cur.phase === 'result') return;
    const id = cur.offer.id, m = { id, game: cur.game, mode: cur.mode, points: 0, skipped: true, chalk: { kind: 'mini', text: '- - -', smear: true } };
    send({ t: 'mini-skip', id, why });
    close();
    onDone(m);
  }
  function escape() {
    if (!cur || cur.kind === 'picker') return close();
    if (cur.kind === 'practice') return backToPicker();
    skip('esc');
  }
  // a leave: the server hears of it from the closed socket or a new door
  function cancel() { offer = null; if (cur?.kind === 'real') { close(); } }

  // ---- the practice room: a picker over the registry, nothing at stake ----
  function openPicker() {
    stopAll(); cur = null; offer = null;
    open('Mini-games');
    cards.replaceChildren(...pickerCards().map(c => {
      const li = el('li', 'mg-card-row'), name = el('span', 'mg-name', c.name), sec = el('span', 'mg-sec', `${c.seconds} s`);
      const go = btn('mg-card', '', () => startPractice(c.id, picked[c.id]));
      go.dataset.game = c.id; go.setAttribute('aria-label', `Play ${c.name}, ${c.seconds} seconds, nothing at stake`);
      const head = el('span', 'mg-head'); head.dataset.game = c.id; head.setAttribute('aria-hidden', 'true'); // the game's head (Aseprite art)
      go.append(head, name, sec);
      const toggles = el('span', 'mg-toggles');
      for (const mode of Object.keys(MODE_WORDS)) {
        const t = btn('mg-mode', MODE_WORDS[mode], () => { picked[c.id] = mode; for (const o of toggles.children) o.setAttribute('aria-pressed', String(o.dataset.mode === mode)); });
        t.dataset.mode = mode; t.setAttribute('aria-pressed', String(picked[c.id] === mode)); toggles.append(t);
      }
      li.append(go, toggles);
      return li;
    }));
    layout({ plateText: "Pick a game. A right call earns a Bonus round, a wrong one can earn a Rescue. Nothing is at stake here.", frameOn: false, cardsOn: true, actionsOn: true });
    show(again, false); show(back, true); say(back, 'Close');
    cur = { kind: 'picker', phase: 'picker' };
  }
  function startPractice(gameId, mode, isAgain = false) {
    stopAll();
    send({ t: 'mini-practice', game: gameId, mode, ...(isAgain ? { again: true } : {}) });
    const text = practicePlateOf(gameId, mode);
    cur = { kind: 'practice', game: gameId, mode, phase: 'play', plateText: text };
    open(gameOf(gameId).NAME);
    layout({ plateText: text, skipOn: true });
    hint(!isAgain && gameId !== 'holden');
    later(() => hint(false), PLATE_MS);
    playStart(seedBytes(Array.from(random(), b => b.toString(16).padStart(2, '0')).join(''), gameOf(gameId).STEPS));
  }
  function practiceEnded() {
    const res = cur.runner.result();
    cur.phase = 'practice-end';
    cur.plateText = platePast(cur.game, res, cur.plateText);
    layout({ plateText: cur.plateText, resultText: practiceLine({ game: cur.game, mode: cur.mode, ...res }), skipOn: false, actionsOn: true, win: res.reachedEnd });
    outro();
    show(again, true); show(back, true); say(back, 'Back');
    again.focus?.();
  }
  function againPractice() { if (cur?.kind === 'practice' && cur.phase === 'practice-end') startPractice(cur.game, cur.mode, true); }
  function backToPicker() { openPicker(); }

  // A frame held still, for the studio's screenshots (/minigames?game=bull&step=40): a rider who keeps to the middle, no server, nothing sent.
  // after=n holds the n-th step past the end (the bell, "UP ONLY!", the throw); input=none lets the rider go; hint=1 shows the hand.
  function demo({ game, mode = 'bonus', step = 0, after = 0, input = 'steady', hint: hintOn = false, shout: shoutOn = false }) {
    if (!gameOf(game)) return openPicker();
    stopAll();
    cur = { kind: 'demo', game, mode, phase: 'demo', plateText: plateOf(game, mode) };
    open(gameOf(game).NAME);
    cur.runner = createRunner({ gameId: game, seed: seedBytes('cd'.repeat(32), gameOf(game).STEPS), mode, start: 0 });
    cur.runner.setInput(obs => (input === 'none' ? (game === 'bear' ? [0, 0] : 0) : game === 'holden' ? demoTap(obs) : game === 'bull' ? Math.max(-4, Math.min(4, Math.round(-(2.5 * obs.lean + 0.8 * obs.vel) / 250))) + 0 : demoDodge(obs)));
    cur.runner.tick(step * STEP_MS);
    const res = cur.runner.result();
    if (res.over) layout({ plateText: platePast(game, res, cur.plateText), resultText: practiceLine({ game, mode, ...res }), win: res.reachedEnd });
    else layout({ plateText: cur.plateText, skipOn: true });
    hint(hintOn);
    if (shoutOn) { action.replaceChildren(word(actionOf(game))); action.hidden = false; root.dataset.action = 'still'; }
    draw(res.over ? after : 0);
  }

  return {
    takeOffer, begin, onSeed, onResult, cancel, openPicker, close, skip, startPractice, demo,
    get phase() { return cur?.phase ?? 'closed'; },
    get open() { return !root.hidden; },
    get runner() { return cur?.runner ?? null; },
    get frameOps() { return cur?.frameOps ?? null; }, // the sprites copied into the last frame (Bull Run), for checks
    get pending() { return offer; },
  };
}
